// What the page of an HTML plan computes from its model and the board (today's
// plans/state.ts, ported): an issue's stage, a condition's being met, a
// phase's state and its fold, and the numbers. Nothing
// here is stored: the body says the structure, the store the states. What
// today's page read of this Mac's own measures (agent time, tokens, the gate)
// is the Code tab's and is left out here (Poulpe-410).
import type { Issue, Label, PlanListing, PlanRow, StateSlug } from '../api/types'
import { keyId } from '../lib/keys'
import type { ExitCondition, PhaseState, PlanItem, PlanModel, PlanPhase, PlanWait, Stage } from './model'

/** The stages a bar draws, in its order; `closed` counts nowhere. */
export type ShownStage = Exclude<Stage, 'closed'>
export const STAGES: readonly ShownStage[] = ['done', 'main', 'flight', 'todo']
export type StageCounts = Record<ShownStage, number>

/** The board's state icon each stage wears in a legend. */
export const STAGE_ICON: Readonly<Record<ShownStage, StateSlug>> = {
  done: 'done',
  main: 'on_main',
  flight: 'in_progress',
  todo: 'todo',
}

/** The board's state icon of a phase's state. */
export const PHASE_ICON: Readonly<Record<PhaseState, StateSlug>> = {
  parked: 'backlog',
  exited: 'done',
  exit_pending: 'on_main',
  in_progress: 'in_progress',
  not_started: 'todo',
}

const STAGE_OF_STATE: Readonly<Record<StateSlug, Stage>> = {
  done: 'done',
  on_main: 'main',
  in_progress: 'flight',
  backlog: 'todo',
  todo: 'todo',
  cancelled: 'closed',
  duplicate: 'closed',
}

/** How far an issue is: done (Done), main (On Main), flight (In Progress), todo (Backlog, Todo), closed (Cancelled, Duplicate). */
export function stageOf(issue: Pick<Issue, 'state'>): Stage {
  return STAGE_OF_STATE[issue.state] ?? 'todo'
}

/** On Main or later. */
export function hasLanded(stage: Stage): boolean {
  return stage === 'done' || stage === 'main'
}

export function emptyCounts(): StageCounts {
  return { done: 0, main: 0, flight: 0, todo: 0 }
}

/** The issue a key names, or undefined. */
export type Resolve = (key: string) => Issue | undefined

const indexes = new WeakMap<ReadonlyMap<string, Issue>, Map<string, Issue>>()

/** The issues by every spelling of their key: one index per map of issues. */
export function resolverOf(issues: ReadonlyMap<string, Issue>): Resolve {
  let index = indexes.get(issues)
  if (!index) {
    index = new Map()
    for (const issue of issues.values()) {
      const id = keyId(issue.key)
      if (id) index.set(id, issue)
    }
    indexes.set(issues, index)
  }
  const found = index
  return (key) => {
    const id = keyId(key)
    return id ? found.get(id) : undefined
  }
}

/** The issues a plan lists that the store holds, in its order: its listing resolved by key. */
export function listedIssues(plan: Pick<PlanRow, 'issues'>, issues: ReadonlyMap<string, Issue>): Issue[] {
  const resolve = resolverOf(issues)
  return plan.issues.flatMap((listing: PlanListing) => {
    const issue = resolve(listing.key)
    return issue ? [issue] : []
  })
}

/** Done of the listed issues, as today's progress counts them: Done, Cancelled and Duplicate are finished. */
export interface Progress {
  done: number
  total: number
}

const FINISHED: ReadonlySet<StateSlug> = new Set(['done', 'cancelled', 'duplicate'])

export function planProgress(plan: Pick<PlanRow, 'issues'>, issues: ReadonlyMap<string, Issue>): Progress {
  const listed = listedIssues(plan, issues)
  return { done: listed.filter((issue) => FINISHED.has(issue.state)).length, total: listed.length }
}

/** The labels the plan's listed issues carry, most carried first, ties by name. A label counts on an issue of its own project and while it lives. */
export function planLabels(plan: Pick<PlanRow, 'issues'>, issues: ReadonlyMap<string, Issue>, labels: ReadonlyMap<string, Label>): Label[] {
  const counts = new Map<string, number>()
  for (const issue of listedIssues(plan, issues)) {
    for (const id of issue.labels) {
      const label = labels.get(id)
      if (label && label.project === issue.project && label.deleted_at === null) counts.set(id, (counts.get(id) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([id, count]) => ({ label: labels.get(id)!, count }))
    .sort((a, b) => b.count - a.count || a.label.name.localeCompare(b.label.name))
    .map((entry) => entry.label)
}

/** The plans' own order: by their place, then their title. */
export function comparePlans(a: PlanRow, b: PlanRow): number {
  if (a.sort_key !== b.sort_key) return a.sort_key < b.sort_key ? -1 : 1
  return a.title.localeCompare(b.title)
}

// ---------------------------------------------------------------- a phase

/** `marked`: the condition carries `data-met`; `derived`: every issue it names is done; null: not met. */
export type Met = 'marked' | 'derived' | null

export interface ConditionFacts {
  condition: ExitCondition
  met: Met
  /** Of the issues it names, how many are done. */
  done: number
  /** How many issues it names. */
  named: number
}

/**
 * A condition is met when it carries `data-met`, or when every issue it
 * names is Done; one that names no issue is never met by itself, and a key
 * that names no issue is not done.
 */
export function conditionFacts(condition: ExitCondition, resolve: Resolve): ConditionFacts {
  const named = condition.keys.length
  let done = 0
  for (const key of condition.keys) {
    const issue = resolve(key)
    if (issue && stageOf(issue) === 'done') done += 1
  }
  const met: Met = condition.metOn !== null ? 'marked' : named > 0 && done === named ? 'derived' : null
  return { condition, met, done, named }
}

export interface WaitFacts {
  wait: PlanWait
  /** It names issues, and all of them have landed: "still waiting?". */
  stale: boolean
}

export function waitFacts(wait: PlanWait, resolve: Resolve): WaitFacts {
  const stale =
    wait.keys.length > 0 &&
    wait.keys.every((key) => {
      const issue = resolve(key)
      return issue !== undefined && hasLanded(stageOf(issue))
    })
  return { wait, stale }
}

export interface Line {
  item: PlanItem
  /** Null when its key names no issue on this board. */
  issue: Issue | null
}

export interface LaneFacts {
  title: string | null
  lines: Line[]
}

export interface PhaseFacts {
  phase: PlanPhase
  state: PhaseState
  conditions: ConditionFacts[]
  /** How many conditions are met. */
  met: number
  waits: WaitFacts[]
  lanes: LaneFacts[]
  /** Its issues, each once, in the order its lanes list them; closed ones too. */
  issues: Issue[]
  /** Its issues by stage; closed ones count nowhere. */
  counts: StageCounts
  /** Its issues that are not closed. */
  total: number
  /** Of those, how many have landed. */
  landed: number
  /** Open at first: folded when not started, parked, exited or every issue landed. */
  open: boolean
}

/**
 * A phase's state: parked; else exited when every condition of its exit is
 * met (a phase without an exit never exits); else exit pending when every
 * issue has landed; else in progress when an issue has started or a
 * condition is met; else not started.
 */
export function phaseFacts(phase: PlanPhase, resolve: Resolve): PhaseFacts {
  const conditions = phase.exit.map((condition) => conditionFacts(condition, resolve))
  const met = conditions.filter((c) => c.met !== null).length
  const waits = phase.waits.map((wait) => waitFacts(wait, resolve))
  const seen = new Set<string>()
  const issues: Issue[] = []
  const lanes = phase.lanes.map((lane) => ({
    title: lane.title,
    lines: lane.items.map((item) => {
      const issue = resolve(item.key) ?? null
      if (issue && !seen.has(issue.id)) {
        seen.add(issue.id)
        issues.push(issue)
      }
      return { item, issue }
    }),
  }))
  const counts = emptyCounts()
  for (const issue of issues) {
    const stage = stageOf(issue)
    if (stage !== 'closed') counts[stage] += 1
  }
  const total = counts.done + counts.main + counts.flight + counts.todo
  const landed = counts.done + counts.main
  const allLanded = total > 0 && landed === total
  const started = counts.done + counts.main + counts.flight > 0 || met > 0
  const state: PhaseState = phase.parked
    ? 'parked'
    : conditions.length > 0 && met === conditions.length
      ? 'exited'
      : allLanded
        ? 'exit_pending'
        : started
          ? 'in_progress'
          : 'not_started'
  return { phase, state, conditions, met, waits, lanes, issues, counts, total, landed, open: openAtFirst(state, total, landed) }
}

/** A phase's computed state alone. */
export function phaseState(phase: PlanPhase, resolve: Resolve): PhaseState {
  return phaseFacts(phase, resolve).state
}

/** Folded when not started, parked, exited or every issue landed; open otherwise. */
export function openAtFirst(state: PhaseState, total: number, landed: number): boolean {
  if (state === 'not_started' || state === 'parked' || state === 'exited') return false
  return !(total > 0 && landed === total)
}

// ---------------------------------------------------------------- the plan

export interface PlanInput {
  model: PlanModel
  /** The issues the plan lists, as the store has them. */
  listed: readonly Issue[]
  /** Every issue the store holds: an item's key is looked up here. */
  issues: ReadonlyMap<string, Issue>
}

export interface PlanFacts {
  /** Every phase, in the body's order. */
  phases: PhaseFacts[]
  /** The plan's issues: those its phases name and those it lists elsewhere, each once. */
  issues: Issue[]
  counts: StageCounts
  /** The plan's issues that are not closed. */
  total: number
  landed: number
  /** Phases that have exited, over how many there are. */
  exited: number
  phaseCount: number
  /** Issues the plan lists that sit in no phase. */
  elsewhere: Issue[]
  /** Keys of items that name no issue on this board, each once. */
  unresolved: string[]
}

export function planFacts({ model, listed, issues }: PlanInput): PlanFacts {
  const resolve = resolverOf(issues)
  const phases = model.phases.map((phase) => phaseFacts(phase, resolve))

  const inPhases = new Set<string>()
  const planIssues: Issue[] = []
  const unresolved: string[] = []
  for (const phase of phases) {
    for (const issue of phase.issues) {
      if (inPhases.has(issue.id)) continue
      inPhases.add(issue.id)
      planIssues.push(issue)
    }
    for (const lane of phase.lanes) {
      for (const line of lane.lines) if (!line.issue && !unresolved.includes(line.item.key)) unresolved.push(line.item.key)
    }
  }
  const elsewhere: Issue[] = []
  const ids = new Set(inPhases)
  for (const listedIssue of listed) {
    const issue = issues.get(listedIssue.id) ?? listedIssue
    if (ids.has(issue.id)) continue
    ids.add(issue.id)
    planIssues.push(issue)
    elsewhere.push(issue)
  }

  const counts = emptyCounts()
  for (const issue of planIssues) {
    const stage = stageOf(issue)
    if (stage !== 'closed') counts[stage] += 1
  }

  return {
    phases,
    issues: planIssues,
    counts,
    total: counts.done + counts.main + counts.flight + counts.todo,
    landed: counts.done + counts.main,
    exited: phases.filter((p) => p.state === 'exited').length,
    phaseCount: phases.length,
    elsewhere,
    unresolved,
  }
}

export interface PlanKpis {
  /** Landed over the plan's issues that are not closed, in whole percent. */
  landedPercent: number
  landed: number
  total: number
  counts: StageCounts
  exited: number
  phaseCount: number
  inFlight: number
}

export function planKpis(facts: PlanFacts): PlanKpis {
  return {
    landedPercent: facts.total > 0 ? Math.round((100 * facts.landed) / facts.total) : 0,
    landed: facts.landed,
    total: facts.total,
    counts: facts.counts,
    exited: facts.exited,
    phaseCount: facts.phaseCount,
    inFlight: facts.counts.flight,
  }
}
